<template>
  <div>
    <b-card>
      <b-card-title class="d-flex align-items-center mb-2">
        <feather-icon icon="TruckIcon" size="24" class="mr-1" />
        Configuração dos Correios
      </b-card-title>
      <b-card-text class="text-muted mb-3">
        Configure as credenciais da API dos Correios para cálculo de frete e geração de etiquetas.
      </b-card-text>

      <b-alert v-if="statusMessage" :variant="statusVariant" show dismissible @dismissed="statusMessage = ''">
        {{ statusMessage }}
      </b-alert>

      <b-form @submit.prevent="salvarConfig">
        <h5 class="mb-1 mt-1">Credenciais da API</h5>
        <b-row>
          <b-col md="6">
            <b-form-group label="Cartão de Postagem" label-for="cartao_postagem">
              <b-form-input
                id="cartao_postagem"
                v-model="form.cartao_postagem"
                placeholder="Número do cartão de postagem"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Contrato" label-for="contrato">
              <b-form-input
                id="contrato"
                v-model="form.contrato"
                placeholder="Número do contrato"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="3">
            <b-form-group label="Número DR" label-for="numero_dr">
              <b-form-input
                id="numero_dr"
                v-model="form.numero_dr"
                placeholder="Ex: 72"
              />
              <small class="text-muted">Regional dos Correios (obrigatório para cálculo de frete)</small>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Usuário" label-for="usuario">
              <b-form-input
                id="usuario"
                v-model="form.usuario"
                placeholder="Usuário da API"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Senha" label-for="senha">
              <b-form-input
                id="senha"
                v-model="form.senha"
                type="password"
                placeholder="Senha da API"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="CEP de Origem" label-for="cep_origem">
              <b-form-input
                id="cep_origem"
                v-model="form.cep_origem"
                placeholder="00000-000"
                v-mask="'#####-###'"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Ambiente" label-for="ambiente">
              <b-form-select
                id="ambiente"
                v-model="form.ambiente"
                :options="ambienteOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" class="d-flex align-items-end pb-1">
            <div>
              <b-badge :variant="tokenAtivo ? 'success' : 'warning'" class="mr-2" pill>
                <feather-icon :icon="tokenAtivo ? 'CheckCircleIcon' : 'AlertCircleIcon'" size="14" class="mr-50" />
                {{ tokenAtivo ? 'Token Ativo' : 'Token Inativo' }}
              </b-badge>
            </div>
          </b-col>
        </b-row>

        <hr />

        <h5 class="mb-1">Endereço do Remetente</h5>
        <b-card-text class="text-muted mb-2" style="font-size: 0.85rem;">
          Endereço de onde os produtos serão enviados. Obrigatório para geração de etiquetas.
        </b-card-text>

        <b-row>
          <b-col md="6">
            <b-form-group label="Nome do Remetente" label-for="remetente_nome">
              <b-form-input
                id="remetente_nome"
                v-model="form.remetente_nome"
                placeholder="Nome ou razão social"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Logradouro" label-for="remetente_logradouro">
              <b-form-input
                id="remetente_logradouro"
                v-model="form.remetente_logradouro"
                placeholder="Rua, Avenida, etc."
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="3">
            <b-form-group label="Número" label-for="remetente_numero">
              <b-form-input
                id="remetente_numero"
                v-model="form.remetente_numero"
                placeholder="Nº"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Complemento" label-for="remetente_complemento">
              <b-form-input
                id="remetente_complemento"
                v-model="form.remetente_complemento"
                placeholder="Sala, Bloco, etc."
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Bairro" label-for="remetente_bairro">
              <b-form-input
                id="remetente_bairro"
                v-model="form.remetente_bairro"
                placeholder="Bairro"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="UF" label-for="remetente_uf">
              <b-form-select
                id="remetente_uf"
                v-model="form.remetente_uf"
                :options="ufOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Cidade" label-for="remetente_cidade">
              <b-form-input
                id="remetente_cidade"
                v-model="form.remetente_cidade"
                placeholder="Cidade"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <hr />

        <div class="d-flex justify-content-between">
          <b-button
            variant="outline-primary"
            :disabled="testando"
            @click="testarConexao"
          >
            <b-spinner v-if="testando" small class="mr-50" />
            <feather-icon v-else icon="WifiIcon" class="mr-50" />
            Testar Conexão
          </b-button>

          <b-button
            variant="primary"
            type="submit"
            :disabled="salvando"
          >
            <b-spinner v-if="salvando" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" class="mr-50" />
            Salvar Configuração
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-card class="mt-2">
      <b-card-title class="d-flex align-items-center mb-2">
        <feather-icon icon="InfoIcon" size="24" class="mr-1" />
        Informações
      </b-card-title>
      <b-card-text>
        <ul class="mb-0">
          <li>O cálculo de frete utiliza a API dos Correios CWS (PAC e SEDEX).</li>
          <li>Caso as credenciais não estejam configuradas, será usado um cálculo estimado.</li>
          <li>O CEP de origem é o endereço de onde os produtos serão enviados.</li>
          <li>O endereço do remetente é obrigatório para gerar etiquetas de envio.</li>
          <li>Use o ambiente <strong>Homologação</strong> para testes e <strong>Produção</strong> para operação real.</li>
          <li>Ao confirmar o pagamento, o sistema gera automaticamente a pré-postagem nos Correios e salva o código de rastreio.</li>
        </ul>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BCardText, BForm, BFormGroup, BFormInput, BFormSelect,
  BRow, BCol, BButton, BAlert, BBadge, BSpinner
} from 'bootstrap-vue'
import axios from '@/libs/axios'

export default {
  components: {
    BCard, BCardTitle, BCardText, BForm, BFormGroup, BFormInput, BFormSelect,
    BRow, BCol, BButton, BAlert, BBadge, BSpinner
  },
  data() {
    return {
      form: {
        cartao_postagem: '',
        contrato: '',
        numero_dr: '',
        usuario: '',
        senha: '',
        cep_origem: '',
        ambiente: 'homologacao',
        remetente_nome: '',
        remetente_logradouro: '',
        remetente_numero: '',
        remetente_complemento: '',
        remetente_bairro: '',
        remetente_cidade: '',
        remetente_uf: '',
      },
      ambienteOptions: [
        { value: 'homologacao', text: 'Homologação (Testes)' },
        { value: 'producao', text: 'Produção' },
      ],
      ufOptions: [
        { value: '', text: 'Selecione...' },
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
        'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
        'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
      ],
      tokenAtivo: false,
      salvando: false,
      testando: false,
      statusMessage: '',
      statusVariant: 'info',
    }
  },
  async mounted() {
    await this.carregarConfig()
  },
  methods: {
    async carregarConfig() {
      try {
        const { data } = await axios.get('/correios/config')
        if (data) {
          this.form = {
            cartao_postagem: data.cartao_postagem || '',
            contrato: data.contrato || '',
            numero_dr: data.numero_dr || '',
            usuario: data.usuario || '',
            senha: data.senha || '',
            cep_origem: data.cep_origem || '',
            ambiente: data.ambiente || 'homologacao',
            remetente_nome: data.remetente_nome || '',
            remetente_logradouro: data.remetente_logradouro || '',
            remetente_numero: data.remetente_numero || '',
            remetente_complemento: data.remetente_complemento || '',
            remetente_bairro: data.remetente_bairro || '',
            remetente_cidade: data.remetente_cidade || '',
            remetente_uf: data.remetente_uf || '',
          }
          this.tokenAtivo = data.token_ativo || false
        }
      } catch (e) {
        console.error('Erro ao carregar config:', e)
      }
    },
    async salvarConfig() {
      this.salvando = true
      try {
        await axios.post('/correios/config', this.form)
        this.statusMessage = 'Configuração salva com sucesso!'
        this.statusVariant = 'success'
        await this.carregarConfig()
      } catch (e) {
        this.statusMessage = e?.response?.data?.error || 'Erro ao salvar configuração'
        this.statusVariant = 'danger'
      } finally {
        this.salvando = false
      }
    },
    async testarConexao() {
      this.testando = true
      try {
        const { data } = await axios.post('/correios/testar-conexao')
        this.statusMessage = data.message
        this.statusVariant = data.success ? 'success' : 'warning'
        if (data.success) this.tokenAtivo = true
      } catch (e) {
        this.statusMessage = e?.response?.data?.message || 'Erro ao testar conexão'
        this.statusVariant = 'danger'
      } finally {
        this.testando = false
      }
    },
  },
}
</script>
